/*
 * Editor shell: a fixed title bar above one scrollable column, with a persistent
 * section rail on desktop. Each section saves itself, so there is no shared
 * footer — Basic Info's save button lives inline in its own section like every
 * other section's.
 */

.paper {
    --editor-rail-width: 216px;
    --editor-gutter: 28px;
    --editor-surface: #fbfcf6;
    --editor-hairline: rgba(17, 24, 39, 0.09);
    --editor-muted: #75736c;
    --editor-accent: #10b981;
    --editor-accent-soft: rgba(16, 185, 129, 0.1);
    --editor-warn: #b45309;

    width: 100%;
    max-width: 900px;
    min-height: 80%;
    background-color: #fff;
}

.content :global(.MuiInputBase-input),
.content :global(.MuiInputLabel-root),
.content :global(.MuiSelect-select) {
    font-size: 14px;
}

.dialogTitle {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    column-gap: 16px;
    padding: 18px var(--editor-gutter) 14px;
    border-bottom: 1px solid var(--editor-hairline);
}

.titleText {
    display: flex;
    flex-direction: column;
    row-gap: 1px;
    min-width: 0;
}

.titleEyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--editor-muted);
}

.titleName {
    font-family: Outfit, sans-serif;
    font-size: 19px;
    font-weight: 600;
    line-height: 1.3;
    color: #1f2937;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.header {
    padding: 16px var(--editor-gutter);
    border-bottom: 1px solid var(--editor-hairline);
    background-color: var(--editor-surface);
}

.body {
    display: flex;
    flex: 1;
    min-height: 0;
}

.sidebar {
    width: var(--editor-rail-width);
    flex-shrink: 0;
    padding: 16px 12px;
    border-right: 1px solid var(--editor-hairline);
    background-color: var(--editor-surface);
    display: flex;
    flex-direction: column;
    row-gap: 2px;
}

.navItem {
    position: relative;
    display: flex;
    align-items: center;
    column-gap: 10px;
    border: none;
    background: none;
    font: inherit;
    font-size: 14px;
    text-align: left;
    color: #4b5563;
    padding: 9px 12px 9px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 120ms ease, color 120ms ease;
}

/* the active marker is a pseudo-element so switching sections cannot reflow the row */
.navItem::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 50%;
    width: 3px;
    height: 0;
    border-radius: 999px;
    background-color: var(--editor-accent);
    transform: translateY(-50%);
    transition: height 160ms ease;
}

.navItem:hover {
    background-color: rgba(17, 24, 39, 0.045);
    color: #1f2937;
}

.navItemActive,
.navItemActive:hover {
    background-color: var(--editor-accent-soft);
    color: #05654a;
    font-weight: 600;
}

.navItemActive::before {
    height: 18px;
}

.navIcon {
    flex-shrink: 0;
    display: flex;
    opacity: 0.75;
}

.navItemActive .navIcon {
    opacity: 1;
}

.navLabel {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* unsaved-work marker, so a dirty section stays visible while editing another one */
.navDot {
    flex-shrink: 0;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #d97706;
    margin-left: auto;
}

.content {
    flex: 1;
    min-width: 0;
    padding: 0 var(--editor-gutter);
}

.section {
    padding: 24px 0;
    scroll-margin-top: 8px;
}

.section:first-child {
    padding-top: 20px;
}

.section + .section {
    border-top: 1px solid var(--editor-hairline);
}

.sectionTitle {
    font-family: Outfit, sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 16px;
}

.sectionScrollSpacer {
    /* Lets the final section title reach the same top alignment as earlier sections. */
    --property-section-title-row-height: 48px;
    height: calc(100% - var(--property-section-title-row-height));
    min-height: 160px;
}

.basicInfoHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 12px;
    margin-bottom: 16px;
}

.basicInfoHeader .sectionTitle {
    margin-bottom: 0;
}

.basicInfoDirtyIndicator {
    color: var(--editor-warn);
    font-weight: 500;
    white-space: nowrap;
}

.basicInfoActions {
    display: flex;
    justify-content: flex-end;
    margin-top: 18px;
}

.linkSaveRow {
    display: flex;
    align-items: flex-start;
    column-gap: 12px;
}

.linkSaveRow > :last-child {
    flex-shrink: 0;
}

.sectionSaveButton {
    height: 37.13px;
}

.mobileNav {
    display: none;
}

@media (max-width: 768px) {
    .paper {
        --editor-gutter: 16px;

        margin: 0;
        width: 100%;
        max-height: 100%;
        height: 100%;
        border-radius: 0;
    }

    .sidebar {
        display: none;
    }

    .mobileNav {
        display: block;
        padding: 12px var(--editor-gutter);
        border-bottom: 1px solid var(--editor-hairline);
        background-color: var(--editor-surface);
    }
}

@media (prefers-reduced-motion: reduce) {
    .navItem,
    .navItem::before {
        transition: none;
    }
}
